import { useEffect, useState } from 'react';
import { AppRouter } from './routes';
import { useTheme, useStore, useIsAuthenticated } from './stores';
import { authAPI } from './services/api';
import { setAuthToken } from './services/api/client';
import { STORAGE_KEYS } from './config/constants';
import { ToastContainer, toastManager } from './components/common/Toast';
import { useStrategyTradeNotifications } from './hooks/useStrategyTradeNotifications';
import type { ToastType } from './components/common/Toast';
import './styles/index.css';

/**
 * Main App component
 */
function App() {
  const theme = useTheme();
  const isAuthenticated = useIsAuthenticated();
  const [toasts, setToasts] = useState<ToastType[]>([]);

  // Subscribe to strategy trade notifications when authenticated
  useStrategyTradeNotifications(isAuthenticated);

  // Apply theme to document
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }
  }, [theme]);

  // Initialize auth from stored token
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      if (!token) {
        useStore.setState((state) => ({
          auth: { ...state.auth, isLoading: false }
        }));
        return;
      }

      try {
        // Set token in API client
        setAuthToken(token);

        // Fetch current user
        const response = await authAPI.me();

        // Update auth state
        useStore.setState((state) => ({
          auth: {
            ...state.auth,
            user: response.user,
            token,
            isAuthenticated: true,
            isLoading: false,
            betaDisclaimer: response.betaDisclaimer ?? null
          }
        }));
      } catch (error) {
        console.error('Failed to restore auth session:', error);
        // Clear invalid token
        setAuthToken(null);
        useStore.setState((state) => ({
          auth: { ...state.auth, isLoading: false }
        }));
      }
    };

    initAuth();
  }, []);

  // Subscribe to toast notifications
  useEffect(() => {
    const unsubscribe = toastManager.subscribe(setToasts);
    return unsubscribe;
  }, []);

  return (
    <>
      <AppRouter />
      <ToastContainer
        toasts={toasts}
        onClose={(id) => toastManager.removeToast(id)}
      />
    </>
  );
}

export default App;
